import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import DistributeUrlsDialog from "./DistributeUrlsDialog";

import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import FolderOffOutlinedIcon from "@mui/icons-material/FolderOffOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import CallSplitIcon from '@mui/icons-material/CallSplit';

/* ---------------------------------------------------------
   Design tokens — kept in sync with Dashboard.jsx.
   If more admin screens adopt this look, worth promoting
   this block to a shared theme/tokens file.
--------------------------------------------------------- */
const COLOR = {
  bg: "#F5F6F4",
  surface: "#FFFFFF",
  ink: "#161A1D",
  inkMuted: "#6B7280",
  inkFaint: "#9AA1A9",
  line: "#E4E7E7",
  accent: "#0B7285",
  accentSoft: "#E3F1F1",
  success: "#2F9E44",
  successSoft: "#E9F7EC",
  danger: "#E0393B",
  dangerSoft: "#FCEAEA",
  warning: "#E8890C",
  warningSoft: "#FDF1E1",
};

const FONT_DISPLAY = "'Space Grotesk', sans-serif";
const FONT_BODY = "'Inter', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

const FONT_IMPORT = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
`;

/* ---------------------------------------------------------
   Static option lists — values are the API contract,
   labels are what people read. Kept separate on purpose.
--------------------------------------------------------- */
const TYPE_OPTIONS = [
  { value: "posts", label: "Post" },
  { value: "pages", label: "Page" },
  { value: "product", label: "Product" },
  { value: "categories", label: "Category" },
  { value: "tag", label: "Tag" },
  { value: "local", label: "Local" },
  { value: "video", label: "Video" },
  { value: "image", label: "Image" },
  { value: "author", label: "Author" },
];

const SOURCE_OPTIONS = [
  { value: "wordpress", label: "WordPress" },
  { value: "node", label: "Node" },
  { value: "custom", label: "Custom" },
];

const NAME_MIN_LENGTH = 3;
const NAME_MAX_LENGTH = 60;
const DESCRIPTION_MAX_LENGTH = 300;
const CHUNK_SIZE_MIN = 1;
const CHUNK_SIZE_MAX = 5000;

const DEFAULT_FORM = {
  name: "",
  slug: "",
  type: "product",
  source: "wordpress",
  description: "",
  chunk_size: 200,
  is_active: true,
};

/* ---------------------------------------------------------
   Pure helpers — no component state, easy to unit test.
--------------------------------------------------------- */
function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function validateForm(form, existingGroups, editingGroupId) {
  const errors = {};
  const name = form.name.trim();
  const slug = form.slug.trim();

  if (!name) {
    errors.name = "Group name is required.";
  } else if (name.length < NAME_MIN_LENGTH) {
    errors.name = `Must be at least ${NAME_MIN_LENGTH} characters.`;
  } else if (name.length > NAME_MAX_LENGTH) {
    errors.name = `Must be under ${NAME_MAX_LENGTH} characters.`;
  }

  if (!slug) {
    errors.slug = "Slug is required.";
  } else if (!/^[a-z0-9]+(-[a-z0-9]+)*$/.test(slug)) {
    errors.slug = "Use lowercase letters, numbers, and single hyphens only.";
  } else {
    const slugTaken = existingGroups.some(
      (g) => g.slug === slug && g.id !== editingGroupId
    );
    if (slugTaken) {
      errors.slug = "This slug is already used by another group.";
    }
  }

  if (!form.type) {
    errors.type = "Type is required.";
  }

  if (!form.source) {
    errors.source = "Source is required.";
  }

  const chunkSize = Number(form.chunk_size);
  if (form.chunk_size === "" || Number.isNaN(chunkSize)) {
    errors.chunk_size = "Chunk size is required.";
  } else if (!Number.isInteger(chunkSize) || chunkSize < CHUNK_SIZE_MIN) {
    errors.chunk_size = `Must be a whole number of at least ${CHUNK_SIZE_MIN}.`;
  } else if (chunkSize > CHUNK_SIZE_MAX) {
    errors.chunk_size = `Must be ${CHUNK_SIZE_MAX} or less.`;
  }

  if (form.description && form.description.length > DESCRIPTION_MAX_LENGTH) {
    errors.description = `Must be under ${DESCRIPTION_MAX_LENGTH} characters.`;
  }

  return errors;
}

/* ---------------------------------------------------------
   Small presentational pieces
--------------------------------------------------------- */
function NodeMark({ size = 20, color = COLOR.accent }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <circle cx="5" cy="6" r="2.4" fill={color} />
      <circle cx="19" cy="6" r="2.4" fill={color} opacity="0.45" />
      <circle cx="12" cy="19" r="2.4" fill={color} opacity="0.8" />
      <path
        d="M7 7.5L10.3 17M17 7.5L13.7 17M7.4 6H16.6"
        stroke={color}
        strokeWidth="1.3"
        strokeLinecap="round"
        opacity="0.5"
      />
    </svg>
  );
}

function TypeChip({ value }) {
  const label = TYPE_OPTIONS.find((t) => t.value === value)?.label || value;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: COLOR.accentSoft,
        color: COLOR.accent,
        fontWeight: 500,
        fontSize: "12px",
      }}
    />
  );
}

function SourceChip({ value }) {
  const isWordpress = value === "wordpress";
  const label = SOURCE_OPTIONS.find((s) => s.value === value)?.label || value;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: isWordpress ? COLOR.successSoft : COLOR.warningSoft,
        color: isWordpress ? COLOR.success : COLOR.warning,
        fontWeight: 500,
        fontSize: "12px",
      }}
    />
  );
}

function SlugChip({ slug }) {
  return (
    <Chip
      size="small"
      variant="outlined"
      label={`${slug}.xml`}
      sx={{
        borderColor: COLOR.line,
        color: COLOR.inkMuted,
        fontFamily: FONT_MONO,
        fontSize: "11.5px",
      }}
    />
  );
}

/* =========================================================
   Main component
========================================================= */
export default function SitemapGroups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});

  const [openDistribute, setOpenDistribute] = useState(false);


  const fetchGroups = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get("/api/v1/sitemap/groups");
      setGroups(data.data || []);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to fetch sitemap groups."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const filteredGroups = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return groups;
    return groups.filter(
      (g) =>
        g.name.toLowerCase().includes(query) ||
        g.slug.toLowerCase().includes(query)
    );
  }, [groups, search]);

  const resetForm = () => {
    setForm(DEFAULT_FORM);
    setErrors({});
    setEditingGroup(null);
    setSlugTouched(false);
  };

  const handleOpenCreate = () => {
    resetForm();
    setOpen(true);
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
    resetForm();
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    updateField("name", value);
    // Auto-fill the slug from the name until the person edits
    // the slug field directly — avoids typing the same thing twice.
    // if (!slugTouched) {
    //   setForm((prev) => ({ ...prev, slug: slugify(value) }));
    // }
  };

  const handleSlugChange = (e) => {
    setSlugTouched(true);
    updateField("slug", e.target.value);
    // updateField("slug", slugify(e.target.value));
  };

  const handleChunkSizeChange = (e) => {
    const raw = e.target.value;
    updateField("chunk_size", raw === "" ? "" : Number(raw));
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setForm({
      name: group.name,
      slug: group.slug,
      type: group.type,
      source: group.source,
      description: group.description || "",
      chunk_size: group.chunk_size,
      is_active: group.is_active,
    });
    setErrors({});
    setSlugTouched(true); // don't overwrite an existing slug on edit
    setOpen(true);
  };

  const saveGroup = async () => {
    const validationErrors = validateForm(form, groups, editingGroup?.id);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const payload = {
      ...form,
      name: form.name.trim(),
      slug: form.slug.trim(),
      description: form.description.trim(),
      chunk_size: Number(form.chunk_size),
    };

    setSaving(true);
    try {
      const url = editingGroup
        ? `/api/v1/sitemap/update-group/${editingGroup.id}`
        : "/api/v1/sitemap/create-group";
      const method = editingGroup ? "put" : "post";

      await axios({ method, url, data: payload });

      toast.success(
        editingGroup ? "Group updated successfully." : "Group created successfully."
      );

      setOpen(false);
      resetForm();
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (group) => {
    const nextActive = !group.is_active;
    // Optimistic update, rolled back on failure.
    setGroups((prev) =>
      prev.map((g) => (g.id === group.id ? { ...g, is_active: nextActive } : g))
    );

    try {
      await axios.put(`/api/v1/sitemap/update-group/${group.id}`, {
        ...group,
        is_active: nextActive,
      });
      toast.success(`Group ${nextActive ? "activated" : "deactivated"} successfully.`);
    } catch (error) {
      setGroups((prev) =>
        prev.map((g) => (g.id === group.id ? { ...g, is_active: group.is_active } : g))
      );
      toast.error(error.response?.data?.message || "Failed to update status.");
    }
  };

  const handleDeleteRequest = (group) => {
    setSelectedGroup(group);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!selectedGroup) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/v1/sitemap/delete-group/${selectedGroup.id}`);
      toast.success("Group deleted successfully.");
      setGroups((prev) => prev.filter((g) => g.id !== selectedGroup.id));
      setDeleteOpen(false);
      setSelectedGroup(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete group.");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <style>{FONT_IMPORT}</style>

      <Card
        sx={{
          borderRadius: "14px",
          border: `1px solid ${COLOR.line}`,
          boxShadow: "none",
          bgcolor: COLOR.surface,
          fontFamily: FONT_BODY,
        }}
      >
        <CardContent sx={{ p: "24px" }}>
          {/* Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mb={3}
          >
            <Box display="flex" alignItems="center" gap="10px">
              <NodeMark />
              <Box>
                <Typography
                  sx={{
                    fontFamily: FONT_DISPLAY,
                    fontWeight: 600,
                    fontSize: "19px",
                    color: COLOR.ink,
                    letterSpacing: "-0.01em",
                    lineHeight: 1.3,
                  }}
                >
                  Sitemap Groups
                </Typography>
                <Typography sx={{ fontSize: "13px", color: COLOR.inkMuted }}>
                  Create and manage sitemap groups.
                </Typography>
              </Box>
            </Box>

            <Box display="flex" gap="10px" flexWrap="wrap">
              <TextField
                size="small"
                placeholder="Search groups…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: COLOR.inkFaint }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  minWidth: "220px",
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                }}
              />

              <Button
                variant="contained"
                disableElevation
                startIcon={<AddIcon />}
                onClick={handleOpenCreate}
                sx={{
                  bgcolor: COLOR.accent,
                  borderRadius: "8px",
                  textTransform: "none",
                  fontWeight: 600,
                  px: "16px",
                  "&:hover": { bgcolor: "#095e6b" },
                }}
              >
                Add group
              </Button>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer
            sx={{ border: `1px solid ${COLOR.line}`, borderRadius: "10px" }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: COLOR.bg } }}>
                  {["Name", "Type", "Source", "Slug", "URLs", "Chunk", "Status", ""].map(
                    (head, i) => (
                      <TableCell
                        key={i}
                        align={head === "" ? "center" : "left"}
                        sx={{
                          fontSize: "11.5px",
                          fontWeight: 600,
                          color: COLOR.inkFaint,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          borderBottom: `1px solid ${COLOR.line}`,
                        }}
                      >
                        {head || "Actions"}
                      </TableCell>
                    )
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                      <CircularProgress size={26} sx={{ color: COLOR.accent }} />
                    </TableCell>
                  </TableRow>
                ) : filteredGroups.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, border: 0 }}>
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        gap="8px"
                        color={COLOR.inkMuted}
                      >
                        <FolderOffOutlinedIcon sx={{ fontSize: 28, color: COLOR.inkFaint }} />
                        <Typography sx={{ fontSize: "14px", fontWeight: 500, color: COLOR.ink }}>
                          {search ? "No groups match your search" : "No sitemap groups yet"}
                        </Typography>
                        <Typography sx={{ fontSize: "13px" }}>
                          {search
                            ? "Try a different name or slug."
                            : "Create your first group to start generating a sitemap."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredGroups.map((group) => (
                    <TableRow
                      key={group.id}
                      hover
                      sx={{ "& td": { borderBottom: `1px solid ${COLOR.line}` } }}
                    >
                      <TableCell sx={{ fontSize: "13.5px", fontWeight: 500, color: COLOR.ink }}>
                        {group.name}
                      </TableCell>
                      <TableCell>
                        <TypeChip value={group.type} />
                      </TableCell>
                      <TableCell>
                        <SourceChip value={group.source} />
                      </TableCell>
                      <TableCell>
                        <SlugChip slug={group.slug} />
                      </TableCell>
<TableCell>

  <Typography
    sx={{
      fontFamily: FONT_MONO,
      fontSize: "13px",
      fontWeight: 600,
      color: COLOR.ink,
    }}
  >
    {group.url_count} / {group.chunk_size}
  </Typography>

  {group.url_count > group.chunk_size && (
    <Box
      sx={{
        mt: 1,
        p: 1,
        borderRadius: 1,
        backgroundColor: "#FFF8E1",
        border: "1px solid #FFE082",
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: "#B26A00",
          display: "block",
          mb: 1,
          lineHeight: 1.5,
        }}
      >
        This sitemap contains more URLs than the configured limit (
        {group.chunk_size}). Create additional sitemap groups and
        redistribute the URLs to keep each sitemap within the limit.
      </Typography>

<Button
  size="small"
  variant="outlined"
  startIcon={<CallSplitIcon />}
  onClick={() => {
    setSelectedGroup(group);
    setOpenDistribute(true);
  }}
>
  Distribute URLs
</Button>
    </Box>
  )}

</TableCell>
                      <TableCell sx={{ fontFamily: FONT_MONO, fontSize: "13px", color: COLOR.inkMuted }}>
                        {group.chunk_size}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={!!group.is_active}
                          onChange={() => handleStatusChange(group)}
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": { color: COLOR.accent },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              bgcolor: COLOR.accent,
                            },
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Edit group">
                          <IconButton
                            size="small"
                            aria-label={`Edit ${group.name}`}
                            onClick={() => handleEdit(group)}
                            sx={{ color: COLOR.accent, mr: "4px" }}
                          >
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete group">
                          <IconButton
                            size="small"
                            aria-label={`Delete ${group.name}`}
                            onClick={() => handleDeleteRequest(group)}
                            sx={{ color: COLOR.danger }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* ================= Create / Edit Dialog ================= */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ fontFamily: FONT_DISPLAY, fontWeight: 600 }}>
          {editingGroup ? "Edit group" : "Create group"}
        </DialogTitle>

        <DialogContent>
          <Box display="grid" gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }} gap={2} mt={1}>
            <TextField
              label="Group name"
              value={form.name}
              onChange={handleNameChange}
              error={!!errors.name}
              helperText={errors.name}
              disabled={saving}
              inputProps={{ maxLength: NAME_MAX_LENGTH }}
              fullWidth
            />

            <TextField
              label="Slug"
              value={form.slug}
              onChange={handleSlugChange}
              error={!!errors.slug}
              helperText={errors.slug || "Lowercase letters, numbers, and hyphens only."}
              disabled={saving}
              fullWidth
            />

            <FormControl fullWidth error={!!errors.type} disabled={saving}>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.type}
                label="Type"
                onChange={(e) => updateField("type", e.target.value)}
              >
                {TYPE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.type && <FormHelperText>{errors.type}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth error={!!errors.source} disabled={saving}>
              <InputLabel>Source</InputLabel>
              <Select
                value={form.source}
                label="Source"
                onChange={(e) => updateField("source", e.target.value)}
              >
                {SOURCE_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.source && <FormHelperText>{errors.source}</FormHelperText>}
            </FormControl>

            <TextField
              type="number"
              label="Chunk size"
              value={form.chunk_size}
              onChange={handleChunkSizeChange}
              error={!!errors.chunk_size}
              helperText={errors.chunk_size || `URLs per sitemap file (max ${CHUNK_SIZE_MAX}).`}
              disabled={saving}
              inputProps={{ min: CHUNK_SIZE_MIN, max: CHUNK_SIZE_MAX }}
              fullWidth
            />

            <Box display="flex" alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={form.is_active}
                    onChange={(e) => updateField("is_active", e.target.checked)}
                    disabled={saving}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: COLOR.accent },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: COLOR.accent,
                      },
                    }}
                  />
                }
                label="Active"
              />
            </Box>

            <Box gridColumn={{ xs: "1", sm: "1 / 3" }}>
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => updateField("description", e.target.value)}
                error={!!errors.description}
                helperText={
                  errors.description ||
                  `${form.description.length}/${DESCRIPTION_MAX_LENGTH} characters`
                }
                disabled={saving}
                multiline
                minRows={2}
                inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
                fullWidth
              />
            </Box>

            <Box
              gridColumn={{ xs: "1", sm: "1 / 3" }}
              sx={{
                bgcolor: COLOR.bg,
                border: `1px solid ${COLOR.line}`,
                borderRadius: "8px",
                p: "10px 14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <LinkOutlinedIcon sx={{ fontSize: 16, color: COLOR.inkFaint }} />
              <Box>
                <Typography sx={{ fontSize: "11px", color: COLOR.inkMuted, lineHeight: 1.4 }}>
                  Sitemap URL
                </Typography>
                <Typography sx={{ fontFamily: FONT_MONO, fontSize: "13px", color: COLOR.ink }}>
                  https://exhibitorsdata.com/{form.slug || "your-slug"}.xml
                </Typography>
              </Box>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={handleClose} disabled={saving} sx={{ textTransform: "none", color: COLOR.inkMuted }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={saving}
            onClick={saveGroup}
            sx={{
              bgcolor: COLOR.accent,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#095e6b" },
            }}
          >
            {saving ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : editingGroup ? (
              "Update group"
            ) : (
              "Create group"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Delete Dialog ================= */}
      <Dialog
        open={deleteOpen}
        onClose={() => !deleting && setDeleteOpen(false)}
        PaperProps={{ sx: { borderRadius: "14px" } }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <WarningAmberOutlinedIcon sx={{ color: COLOR.danger }} />
          Delete sitemap group
        </DialogTitle>

        <DialogContent>
          <Typography sx={{ fontSize: "14px", color: COLOR.ink }}>
            Are you sure you want to delete <b>{selectedGroup?.name}</b>? This will
            remove the group and stop{" "}
            <span style={{ fontFamily: FONT_MONO }}>{selectedGroup?.slug}.xml</span> from
            being generated. This can't be undone.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setDeleteOpen(false)}
            disabled={deleting}
            sx={{ textTransform: "none", color: COLOR.inkMuted }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            disabled={deleting}
            onClick={confirmDelete}
            sx={{
              bgcolor: COLOR.danger,
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { bgcolor: "#c22f31" },
            }}
          >
            {deleting ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Distribute URLs Dialog ================= */}
        <DistributeUrlsDialog
          open={openDistribute}
          onClose={() => setOpenDistribute(false)}
          group={selectedGroup}
          onSuccess={fetchGroups}
        />
    </>
  );
}